/**
 * The payment panel's pure half (P6-T2).
 *
 * A **payment** is a protected transfer pair whose money leaves a cash account and arrives in
 * a card (or another cash account): the pair model, its two signed legs and the eligibility
 * rule are P6-T1's and are consumed here exactly as they shipped. Nothing in this module
 * decides money: every figure it produces is either the API's own recorded balance or the
 * pair's own legs, added together with `bigint` — never a float.
 *
 * Three things are computed here rather than in JSX, so the fast suite can assert them:
 *
 *   - **both accounts' impacts**, each as `before → effect → after`. `effect` is what this
 *     preview really moves *today*, which is not the same as the leg: a leg moves the recorded
 *     balance only when its parent is completed and its shared date has arrived, so a planned
 *     payment correctly shows no movement, and a deletion of an uncounted pair correctly shows
 *     none either;
 *   - the **payment position** against a card destination's debt — partial, settles, excess or
 *     already-in-credit — which is the surface's own answer to "an overpayment creates credit,
 *     not clamped debt". The card's own state word comes from the same signed figure it is
 *     printed beside (`view.ts`, CARD-03);
 *   - the **form mapping**: a same-account pair, a card source, a cross-currency pair, a
 *     zero/negative amount, an empty description and a malformed date are refused before any
 *     request is made. The API refuses all of them again — it is the authority — this layer
 *     exists so the ordinary mistake costs no round trip.
 */

import type {
  AccountBody,
  FxInputMode,
  TransferBody,
  TransferCompleteRequest,
  TransferCreateRequest,
  TransferDeleteRequest,
  TransferFxCreateRequest,
  TransferLegBody,
  TransferPairSuggestionBody,
  TransferPreviewBody,
  TransferStatus,
} from '@saldo/contracts';
import { SUPPLIED_RATE_PATTERN } from '@saldo/contracts';
import { parseBusinessDate } from '@saldo/domain';

import type { Mapped } from './forms';
import { formatAmountInput, formatMoneyMagnitude, parseAmountToMinor } from './money';
import { cardState, type CardState } from './view';

const DESCRIPTION_LIMIT = 500;

/** The payment list read's bound; the API's own maximum (`transferListQuerySchema`'s response). */
export const TRANSFER_PAGE_LIMIT = 200;

/** The account roles a payment may move money between: cash→cash and cash→card (`TRF-01`). */
const PAYMENT_SOURCE_ROLE = 'cash';

/** A strict business date, or an error naming the field — never a silent fallback to today. */
function businessDate(value: string, field: string): Mapped<string> {
  try {
    parseBusinessDate(value);
    return { ok: true, body: value };
  } catch {
    return { ok: false, error: `Enter a valid ${field} (YYYY-MM-DD).` };
  }
}

/* --------------------------------------------------------------------- the form --- */

export interface PaymentForm {
  /** The account the money leaves; `TRF-01` supports a cash source only. */
  readonly sourceAccountId: string;
  readonly destinationAccountId: string;
  /** What the user typed: a positive magnitude, e.g. `40.00`. */
  readonly amount: string;
  readonly description: string;
  readonly transferDate: string;
  readonly status: TransferStatus;
  /**
   * How the pair **states its money** (P7-T3): the same currency on both sides, the rate, or the
   * arriving amount.
   *
   * It is part of the form rather than inferred from the two accounts alone, because that is what
   * "no implicit parity and no implicit rate" means at the surface: two different currencies do not
   * quietly become a rate of some default, and two equal currencies never become a rate of `1`.
   */
  readonly rateMode: PaymentRateMode;
  /** The supplied rate, read in `rate` mode only. */
  readonly rate: string;
  /** The arriving magnitude, read in `destination` mode only. */
  readonly destinationAmount: string;
}

/** Which of the pair's two operands the form states. */
export type PaymentRateMode = 'same' | 'rate' | 'destination';

/**
 * The `transfer.create` preview request for a payment.
 *
 * The amount is a **positive magnitude** and the pair's own normalization is what turns it
 * into one negative outgoing and one positive incoming leg — the one place the request's
 * "what I want to move" becomes the pair's signed money (`@saldo/domain`'s
 * `buildTransferLegs`), so no direction-plus-money duplicate can be submitted here.
 */
export function toPaymentCreate(
  form: PaymentForm,
  accounts: readonly AccountBody[],
): Mapped<TransferCreateRequest> {
  if (form.sourceAccountId === '') return { ok: false, error: 'Choose the account the money leaves.' };
  if (form.destinationAccountId === '') {
    return { ok: false, error: 'Choose the account the money arrives in.' };
  }
  if (form.sourceAccountId === form.destinationAccountId) {
    return { ok: false, error: 'A payment moves money between two different accounts.' };
  }

  const source = accounts.find((account) => account.id === form.sourceAccountId);
  const destination = accounts.find((account) => account.id === form.destinationAccountId);
  if (source === undefined) return { ok: false, error: 'Choose the account the money leaves.' };
  if (destination === undefined) return { ok: false, error: 'Choose the account the money arrives in.' };
  if (source.kind !== PAYMENT_SOURCE_ROLE) {
    return { ok: false, error: 'A payment leaves a cash account, never a card.' };
  }
  if (source.currency !== destination.currency) {
    return {
      ok: false,
      error: `A payment is same-currency: ${source.name} is ${source.currency} and ${destination.name} is ${destination.currency}.`,
    };
  }

  const sourceMinor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (sourceMinor === null) {
    return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };
  }

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description.length > DESCRIPTION_LIMIT) {
    return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
  }

  const date = businessDate(form.transferDate, 'payment date');
  if (!date.ok) return date;

  return {
    ok: true,
    body: {
      action: 'transfer.create',
      description,
      transferDate: date.body,
      status: form.status,
      sourceAccountId: source.id,
      destinationAccountId: destination.id,
      sourceMinor,
    },
  };
}

/**
 * The payment request the form states, whichever pair it is.
 *
 * This is the surface's half of "no implicit parity and no implicit rate": the currency pair and
 * `rateMode` must **agree**, and a disagreement is a refusal with a sentence rather than a silent
 * coercion. Equal currencies with a rate asked for, or two different currencies with no operand
 * stated, are both refused here — and the API refuses both again, because it is the authority.
 *
 * A cross-currency payment is cash→cash: a card is not a currency counterparty, and no FX card
 * payment exists in the contract.
 */
export function toPaymentRequest(
  form: PaymentForm,
  accounts: readonly AccountBody[],
): Mapped<TransferCreateRequest | TransferFxCreateRequest> {
  if (form.sourceAccountId === '') return { ok: false, error: 'Choose the account the money leaves.' };
  if (form.destinationAccountId === '') {
    return { ok: false, error: 'Choose the account the money arrives in.' };
  }
  if (form.sourceAccountId === form.destinationAccountId) {
    return { ok: false, error: 'A payment moves money between two different accounts.' };
  }

  const source = accounts.find((account) => account.id === form.sourceAccountId);
  const destination = accounts.find((account) => account.id === form.destinationAccountId);
  if (source === undefined) return { ok: false, error: 'Choose the account the money leaves.' };
  if (destination === undefined) return { ok: false, error: 'Choose the account the money arrives in.' };
  if (source.kind !== PAYMENT_SOURCE_ROLE) {
    return { ok: false, error: 'A payment leaves a cash account, never a card.' };
  }

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description.length > DESCRIPTION_LIMIT) {
    return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
  }
  const date = businessDate(form.transferDate, 'payment date');
  if (!date.ok) return date;

  const sourceMinor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (sourceMinor === null) {
    return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };
  }

  const shared = {
    description,
    transferDate: date.body,
    status: form.status,
    sourceAccountId: source.id,
    destinationAccountId: destination.id,
    sourceMinor,
  } as const;

  if (source.currency === destination.currency) {
    if (form.rateMode !== 'same') {
      return {
        ok: false,
        error: `Both accounts are ${source.currency}, so there is no rate to state: a same-currency payment assumes no parity.`,
      };
    }
    return { ok: true, body: { action: 'transfer.create', ...shared } };
  }

  if (form.rateMode === 'same') {
    return {
      ok: false,
      error: `This payment crosses ${source.currency} to ${destination.currency}: state the rate or the arriving amount — nothing is assumed.`,
    };
  }
  if (destination.kind !== 'cash') {
    return {
      ok: false,
      error: 'A cross-currency payment is cash to cash: a card is never a currency counterparty.',
    };
  }
  if (form.rateMode === 'destination') {
    const destinationMinor = parseAmountToMinor(form.destinationAmount, {
      allowNegative: false,
      allowZero: false,
    });
    if (destinationMinor === null) {
      return {
        ok: false,
        error: 'Enter the arriving amount as a positive amount with at most two decimal places.',
      };
    }
    return {
      ok: true,
      body: { action: 'transfer.fxCreate', ...shared, fxInputMode: 'destination', destinationMinor },
    };
  }

  const rate = form.rate.trim();
  if (!new RegExp(SUPPLIED_RATE_PATTERN).test(rate)) {
    return {
      ok: false,
      error:
        'Enter the rate as a plain positive decimal, e.g. 5.4 — at most 12 integer and 12 fractional digits.',
    };
  }
  // A zero rate is a plain decimal the pattern admits and the domain refuses; refusing it here
  // saves the round trip without becoming the authority for it.
  if (!/[1-9]/.test(rate)) {
    return { ok: false, error: 'The rate must be greater than zero.' };
  }
  return {
    ok: true,
    body: { action: 'transfer.fxCreate', ...shared, fxInputMode: 'rate', suppliedRate: rate },
  };
}

/**
 * The `transfer.complete` preview request: the manual completion date.
 *
 * The chosen date is the pair's **shared** date — both legs move with it and neither carries
 * a date of its own — so completing a plan early or late is one operation on one date.
 */
export function toPaymentComplete(
  transfer: Pick<TransferBody, 'id' | 'status' | 'canceled'>,
  transferDate: string,
): Mapped<TransferCompleteRequest> {
  if (transfer.canceled) return { ok: false, error: 'This payment was canceled.' };
  if (transfer.status !== 'planned') return { ok: false, error: 'This payment is already completed.' };
  const date = businessDate(transferDate, 'completion date');
  if (!date.ok) return date;
  return { ok: true, body: { action: 'transfer.complete', transferId: transfer.id, transferDate: date.body } };
}

/** The `transfer.delete` preview request: the whole pair, never a single leg. */
export function toPaymentDelete(
  transfer: Pick<TransferBody, 'id' | 'canceled'>,
): Mapped<TransferDeleteRequest> {
  if (transfer.canceled) return { ok: false, error: 'This payment was already canceled.' };
  return { ok: true, body: { action: 'transfer.delete', transferId: transfer.id } };
}

/* ---------------------------------------------------------------- the impacts --- */

/**
 * Whether a pair of this status and date is part of the recorded balance at `businessToday`.
 *
 * This is the same rule the API's own balance read applies to a transfer leg
 * (`ACCOUNT_SELECT`: `status = 'completed' and transfer_date <= cutoff`), restated here because
 * the panel has to answer "what does this preview move *today*" and the honest answer for a
 * plan is "nothing".
 */
export function countsAt(status: TransferStatus, transferDate: string, businessToday: string): boolean {
  return status === 'completed' && transferDate <= businessToday;
}

/** How a payment sits against the destination card's debt. */
export type CardPosition = 'partial' | 'settles' | 'excess' | 'credit';

/**
 * The position of one payment against what the card owes *before* it.
 *
 * `owed` is the magnitude of a negative card balance. A payment below it leaves debt behind
 * (partial), equal to it settles the card, and above it leaves the card in credit — the
 * overpayment case, which is a positive balance rather than a clamped zero.
 */
export function cardPosition(beforeMinor: bigint, paymentMinor: bigint): CardPosition {
  if (beforeMinor >= 0n) return 'credit';
  const owed = -beforeMinor;
  if (paymentMinor < owed) return 'partial';
  if (paymentMinor === owed) return 'settles';
  return 'excess';
}

/** The sentence the panel prints beside the position. */
export function cardPositionLabel(
  position: CardPosition,
  owedMinor: bigint,
  paymentMinor: bigint,
  currency: string,
): string {
  switch (position) {
    case 'partial':
      return `Partial — ${formatMoneyMagnitude(owedMinor - paymentMinor, currency)} of the card's debt stays on the card.`;
    case 'settles':
      return 'Settles the card — the card\u2019s balance becomes zero.';
    case 'excess':
      return `Excess — this is ${formatMoneyMagnitude(paymentMinor - owedMinor, currency)} more than the card owes, so the card ends in credit rather than in debt.`;
    case 'credit':
      return 'The card is already in credit, so this payment adds to that credit.';
  }
}

/** One account's side of a previewed payment. */
export interface PaymentImpact {
  readonly accountId: string;
  readonly accountName: string;
  readonly accountKind: 'cash' | 'card';
  readonly side: 'outgoing' | 'incoming';
  readonly currency: string;
  /** The leg as the preview writes it, signed. */
  readonly legMinor: bigint;
  /** What this preview moves in the account **today**: `0` for a plan, and for a deletion of an uncounted pair. */
  readonly effectMinor: bigint;
  /** The API's own recorded balance at the business day, before this preview — `null` when the
   * accounts read this panel holds does not carry the account. Never a guessed zero: a zero would
   * be printed as a real balance. */
  readonly beforeMinor: bigint | null;
  readonly afterMinor: bigint | null;
  /** The signed figure's own word, so a card label cannot disagree with the number beside it. */
  readonly afterState: CardState | null;
}

/** When a payment's date sits against the business day. */
export type PaymentTiming = 'earlier' | 'today' | 'ahead';

export function paymentTiming(transferDate: string, businessToday: string): PaymentTiming {
  if (transferDate < businessToday) return 'earlier';
  if (transferDate > businessToday) return 'ahead';
  return 'today';
}

export function paymentTimingLabel(timing: PaymentTiming): string {
  switch (timing) {
    case 'earlier':
      return 'Dated before the business day — an early payment: it counts from its own date.';
    case 'today':
      return 'Dated on the business day.';
    case 'ahead':
      return 'Dated after the business day — it is not today\u2019s money, and a plan moves nothing at all.';
  }
}

/** The stored preview, as the panel reviews it before anything is committed. */
export interface PaymentPreviewView {
  readonly id: string;
  readonly action: TransferPreviewBody['action'];
  readonly transferId: string | null;
  readonly revision: string | null;
  readonly businessToday: string;
  readonly expiresAt: string;
  readonly status: TransferStatus;
  readonly transferDate: string;
  readonly description: string;
  readonly currency: string;
  /** True when the previewed action cancels the whole pair. */
  readonly canceled: boolean;
  readonly affectedEntryIds: readonly string[];
  readonly timing: PaymentTiming;
  /** Whether the pair this preview would write is part of the recorded balance at the business day. */
  readonly countedToday: boolean;
  /** Both accounts, in the preview's own leg order (outgoing first). */
  readonly impacts: readonly PaymentImpact[];
  /** The magnitude the money moves, as a positive figure. */
  readonly amountMinor: bigint;
  /** The destination card's debt before the payment (positive = owed), or `null` when it is not a card. */
  readonly destinationOwedMinor: bigint | null;
  /** The payment's position against that debt; `null` when it is not a card, or when nothing moves today. */
  readonly cardPosition: CardPosition | null;
  readonly positionLabel: string | null;
  /** The cross-currency figures the pair states, or `null` on a same-currency pair. */
  readonly fx: PaymentFxView | null;
}

/** The FX half of a reviewed preview, when the pair crosses two currencies (P7-T3). */
export interface PaymentFxView {
  readonly inputMode: FxInputMode;
  /** The direction word from the API: "CAD per USD" — destination units per source unit (FX-05). */
  readonly directionLabel: string;
  /** `1 USD = 5.400000 CAD`, the labeled figure (FX-05). */
  readonly notation: string;
  /** The exact rate the user supplied, or `null` in destination mode (which stores no ratio). */
  readonly suppliedRate: string | null;
  /** True when six places do not capture the exact ratio. */
  readonly approximate: boolean;
  /** The sentence that keeps a display figure from reading as money. */
  readonly note: string;
}

/**
 * The FX block of a stored preview, as the panel shows it.
 *
 * The figure is *rendered* from the API's own derived rate and is labelled with its direction and
 * its approximation; the two authoritative amounts are the legs, which the impacts already print.
 * This module does no rate arithmetic — it has no business deriving money a second time.
 */
export function paymentFxView(preview: TransferPreviewBody): PaymentFxView | null {
  if (preview.fx === undefined) return null;
  const { fx } = preview;
  return {
    inputMode: fx.inputMode,
    directionLabel: fx.directionLabel,
    notation: `1 ${fx.sourceCurrency} = ${fx.displayRate} ${fx.destinationCurrency}`,
    suppliedRate: fx.suppliedRate,
    approximate: fx.displayRateApproximate,
    note: fx.displayRateApproximate
      ? `Shown to six places, ${fx.directionLabel}, and not exact — the two amounts are the money.`
      : `Exact to six places, ${fx.directionLabel}. The two amounts are the money.`,
  };
}

/**
 * One offered pair, as the create form reads it (TRF-06).
 *
 * Every field here is a *previous* value, and nothing applies it: the panel paints these, and the
 * user's own click is what copies them into the form. That is the whole difference between a
 * reviewed reuse and an implicit one, so this module deliberately has no function that mutates a
 * form on its own.
 */
export interface PairSuggestionView {
  readonly sourceAccountId: string;
  readonly destinationAccountId: string;
  readonly label: string;
  readonly detail: string;
  readonly rateMode: PaymentRateMode;
  readonly rate: string;
  readonly destinationAmount: string;
}

/** The offered pair's own sentence, with its last status and how often the pair was used. */
export function pairSuggestionView(suggestion: TransferPairSuggestionBody): PairSuggestionView {
  const rateMode: PaymentRateMode =
    suggestion.lastInputMode === null ? 'same' : suggestion.lastInputMode;
  const sameCurrency = suggestion.sourceCurrency === suggestion.destinationCurrency;
  const detail =
    suggestion.lastInputMode === null || sameCurrency
      ? `Last used ${suggestion.lastTransferDate} · ${suggestion.lastStatus} · ${suggestion.useCount}×`
      : `Last used ${suggestion.lastTransferDate} · ${suggestion.lastStatus} · ${suggestion.useCount}× · last rate ${suggestion.lastSuppliedRate ?? 'derived from the amounts'}`;
  return {
    sourceAccountId: suggestion.sourceAccountId,
    destinationAccountId: suggestion.destinationAccountId,
    label: `${suggestion.sourceAccountName} → ${suggestion.destinationAccountName}`,
    detail,
    rateMode,
    rate: suggestion.lastSuppliedRate ?? '',
    destinationAmount: formatAmountInput(BigInt(suggestion.lastDestinationMinor)),
  };
}

/**
 * The values a chosen suggestion copies into the create form — the **only** way a suggestion
 * reaches a request, and it is called from a click handler and nowhere else.
 *
 * The amounts come from the pair's own last legs, so what the user reviews is what they moved last
 * time rather than a figure this layer invented. The source amount is deliberately **not** copied:
 * only the operands the reuse is about (the two accounts, the mode, and the mode's own operand)
 * are, so the money being moved is always restated by the user.
 */
export function applySuggestion(view: PairSuggestionView): Partial<PaymentForm> {
  return {
    sourceAccountId: view.sourceAccountId,
    destinationAccountId: view.destinationAccountId,
    rateMode: view.rateMode,
    rate: view.rate,
    destinationAmount: view.rateMode === 'destination' ? view.destinationAmount : '',
  };
}

/**
 * Reduce a stored transfer preview to the figures the panel reviews.
 *
 * `prior` is the pair as it stands — the payments list read that opened this preview — or
 * `null` for a creation. It is what makes the arithmetic exact rather than approximate: a
 * preview that changes an already-counted pair revises the balance by the *difference*
 * between its legs and the stored ones, and one that deletes it removes exactly what was
 * counted.
 *
 * An account the `accounts` read does not carry contributes **no** before-figure — the impact
 * reports `null` and the panel says the account was not read — rather than a zero that would be
 * printed as if it were a recorded balance. Nothing is invented to fill the gap.
 */
export function paymentPreviewView(
  preview: TransferPreviewBody,
  prior: TransferBody | null,
  accounts: readonly AccountBody[],
  destinationAccountId: string | null,
): PaymentPreviewView {
  const byId = new Map(accounts.map((account) => [account.id, account]));
  const countedBefore = prior !== null && !prior.canceled && countsAt(prior.status, prior.transferDate, preview.businessToday);
  const countedAfter = !preview.canceled && countsAt(preview.status, preview.transferDate, preview.businessToday);

  const impacts: PaymentImpact[] = preview.legs.map((leg) => {
    const account = byId.get(leg.accountId);
    const beforeMinor = account === undefined ? null : BigInt(account.balance.minorUnits);
    const priorLeg = prior?.legs.find(
      (candidate) => candidate.accountId === leg.accountId && candidate.side === leg.side,
    );
    const effectMinor =
      (countedAfter ? BigInt(leg.signedMinor) : 0n) -
      (countedBefore && priorLeg !== undefined ? BigInt(priorLeg.signedMinor) : 0n);
    const afterMinor = beforeMinor === null ? null : beforeMinor + effectMinor;
    return {
      accountId: leg.accountId,
      accountName: account?.name ?? leg.accountId,
      accountKind: leg.accountKind,
      side: leg.side,
      currency: leg.currency,
      legMinor: BigInt(leg.signedMinor),
      effectMinor,
      beforeMinor,
      afterMinor,
      afterState: afterMinor === null ? null : cardState(afterMinor),
    };
  });

  const incoming = impacts.find((impact) => impact.side === 'incoming');
  const destination =
    destinationAccountId === null ? undefined : byId.get(destinationAccountId);
  const amountMinor = incoming === undefined ? 0n : incoming.legMinor;
  const isCardDestination = destination?.kind === 'card';
  const owedMinor =
    incoming !== undefined && incoming.beforeMinor !== null && incoming.beforeMinor < 0n
      ? -incoming.beforeMinor
      : 0n;
  const position =
    isCardDestination && countedAfter && incoming !== undefined && incoming.beforeMinor !== null
      ? cardPosition(incoming.beforeMinor, amountMinor)
      : null;

  return {
    id: preview.id,
    action: preview.action,
    transferId: preview.transferId ?? null,
    revision: preview.revision ?? null,
    businessToday: preview.businessToday,
    expiresAt: preview.expiresAt,
    status: preview.status,
    transferDate: preview.transferDate,
    description: preview.description,
    currency: preview.currency,
    canceled: preview.canceled,
    affectedEntryIds: preview.affectedEntryIds,
    timing: paymentTiming(preview.transferDate, preview.businessToday),
    countedToday: countedAfter,
    impacts,
    amountMinor,
    destinationOwedMinor: isCardDestination ? owedMinor : null,
    cardPosition: position,
    positionLabel:
      position === null ? null : cardPositionLabel(position, owedMinor, amountMinor, preview.currency),
    fx: paymentFxView(preview),
  };
}

/* ------------------------------------------------------------------ the list --- */

/** The account's own leg of a pair, or `undefined` when this pair does not touch it. */
export function legOf(transfer: TransferBody, accountId: string): TransferLegBody | undefined {
  return transfer.legs.find((leg) => leg.accountId === accountId);
}

/** The other account of the pair, or `undefined` when this read cannot name it. */
export function counterpartOf(
  transfer: TransferBody,
  accountId: string,
  accounts: readonly AccountBody[],
): AccountBody | undefined {
  const other = transfer.legs.find((leg) => leg.accountId !== accountId);
  return other === undefined ? undefined : accounts.find((account) => account.id === other.accountId);
}

/** One row of an account's payments list. */
export interface PaymentRow {
  readonly id: string;
  readonly description: string;
  readonly transferDate: string;
  readonly status: TransferStatus;
  readonly canceled: boolean;
  /** This account's own signed leg, **in the leg's own currency** — not the pair's source one. */
  readonly legMinor: bigint;
  readonly currency: string;
  /** The other account's name, or `null` when the pair does not touch this list's account. */
  readonly counterpartName: string | null;
  /** A live plan: it can be completed on a chosen date. */
  readonly planned: boolean;
  /** A pair still in force: any pairing, plan or completed, can be cancelled whole. */
  readonly cancelable: boolean;
}

/**
 * An account's pairs as the list renders them, newest first — the API's own order, restated so
 * a re-render cannot reorder the list differently from the read.
 *
 * A pair that does not touch the account (the API scopes the read, so this cannot normally
 * happen) is dropped rather than shown with a fabricated leg.
 *
 * The row's figure is this account's **own leg**, printed in **that leg's own currency**: a
 * cross-currency pair's two legs are denominated separately, so the parent's `currency` — the
 * pair's source side — would print an arriving amount in the currency that never arrived.
 */
export function paymentRows(
  transfers: readonly TransferBody[],
  accountId: string,
  accounts: readonly AccountBody[],
): PaymentRow[] {
  const rows: PaymentRow[] = [];
  for (const transfer of transfers) {
    const leg = legOf(transfer, accountId);
    if (leg === undefined) continue;
    rows.push({
      id: transfer.id,
      description: transfer.description,
      transferDate: transfer.transferDate,
      status: transfer.status,
      canceled: transfer.canceled,
      legMinor: BigInt(leg.signedMinor),
      currency: leg.currency,
      counterpartName: counterpartOf(transfer, accountId, accounts)?.name ?? null,
      planned: transfer.status === 'planned' && !transfer.canceled,
      cancelable: !transfer.canceled,
    });
  }
  return rows.sort((left, right) =>
    left.transferDate === right.transferDate
      ? left.id.localeCompare(right.id)
      : right.transferDate.localeCompare(left.transferDate),
  );
}
